import { useCallback, useEffect, useRef, useState } from "react";
import { BOOK_CONTENT_TYPES, bookContentType, type Book, type BookStatus } from "@baros/core";
import { useApp } from "../context.js";
import type { ScreenProps } from "../screens.js";
import { booksApi, BooksApiError, type Shelf } from "./api.js";
import "./books.css";

/** Re-read the shelf this often while anything is uploading, indexing or being removed. */
const POLL_MS = 10_000;

const STATUS: Readonly<Record<BookStatus, { readonly label: string; readonly tone: string }>> = {
  uploading: { label: "Uploading", tone: "warn" },
  syncing: { label: "Indexing", tone: "warn" },
  ready: { label: "Ready", tone: "pos" },
  failed: { label: "Failed", tone: "neg" },
  deleting: { label: "Removing", tone: "warn" },
};

function size(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function dollars(cents: number): string {
  return cents < 1 && cents > 0 ? "under $0.01" : `$${(cents / 100).toFixed(2)}`;
}

function message(err: unknown): string {
  return err instanceof BooksApiError || err instanceof Error ? err.message : "That did not work";
}

/**
 * Screen 14 — Books: the development chat's reference shelf (D11).
 *
 * Managers, owners and admins put recipe books here — PDFs, digital or
 * scanned, or plain-text and Markdown files that skip extraction — and the
 * chat searches them. The registry hides the screen without `books:manage`
 * and every route re-checks it.
 *
 * Indexing takes minutes and is not ours to watch, so the screen polls the
 * shelf while anything is in flight; the list call is also what moves books
 * from indexing to ready on the server.
 */
export function BooksScreen(_props: ScreenProps) {
  const app = useApp();
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  /** A book id mid-action, or "upload". */
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setShelf(await booksApi.shelf(app.token));
    } catch (err) {
      setError(message(err));
    }
  }, [app.token]);

  useEffect(() => {
    void load();
  }, [load]);

  const inFlight = shelf?.books.some((b) => b.status !== "ready" && b.status !== "failed") ?? false;
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [inFlight, load]);

  const pick = (f: File | null) => {
    setError(null);
    setMsg(null);
    if (f && !bookContentType(f.type, f.name)) {
      setError(`${f.name} is not a PDF, .txt or .md file.`);
      setFile(null);
      return;
    }
    setFile(f);
    setTitle(f ? f.name.replace(/\.[^.]+$/, "") : "");
  };

  const upload = async () => {
    if (!file) return;
    setBusy("upload");
    setError(null);
    setMsg(null);
    setProgress(0);
    try {
      await booksApi.add(app.token, file, title.trim(), setProgress);
      setMsg(
        `${title.trim() || file.name} is uploaded and queued for indexing. ` +
          (file.type === "application/pdf" || /\.pdf$/i.test(file.name)
            ? "A long or scanned PDF can take several minutes."
            : "Text files index quickly."),
      );
      setFile(null);
      setTitle("");
      if (picker.current) picker.current.value = "";
      await load();
    } catch (err) {
      setError(message(err));
      await load();
    } finally {
      setBusy(null);
      setProgress(null);
    }
  };

  const act = async (bookId: string, action: () => Promise<unknown>, done: string) => {
    setBusy(bookId);
    setError(null);
    setMsg(null);
    try {
      await action();
      setMsg(done);
      await load();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  };

  if (error && !shelf) {
    return (
      <div className="pad">
        <div className="msg err">{error}</div>
      </div>
    );
  }
  if (!shelf) return <div className="pad label">Loading the shelf…</div>;

  const ready = shelf.books.filter((b) => b.status === "ready").length;

  return (
    <div className="screen">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div className="label">
            {ready} ready for the chat · {size(shelf.totalBytes)}
          </div>
          <div className="serif" style={{ fontSize: 28, lineHeight: 1.1, marginTop: 4 }}>
            Books
          </div>
        </div>
      </div>

      <div className="scroll pad">
        {error && <div className="msg err" style={{ marginBottom: 16 }}>{error}</div>}
        {msg && <div className="msg ok" style={{ marginBottom: 16 }}>{msg}</div>}

        <div className="bk-section">
          <h3>Add a book</h3>
          <div className="bk-note">
            PDFs — digital or scanned — or plain-text and Markdown files. Text files skip reading and
            OCR entirely and cost the least to keep. The development chat searches everything here
            that is ready, and cites the book (and page, for PDFs) it took a passage from.
          </div>
          <div className="bk-card">
            <div className="bk-grid">
              <label className="field">
                <span>File</span>
                <input
                  ref={picker}
                  type="file"
                  accept={[".pdf", ".txt", ".md", ...BOOK_CONTENT_TYPES].join(",")}
                  disabled={busy === "upload"}
                  onChange={(e) => pick(e.target.files?.[0] ?? null)}
                />
              </label>
              <label className="field">
                <span>Title</span>
                <input
                  type="text"
                  value={title}
                  maxLength={160}
                  placeholder="The Fine Art of Mixing Drinks"
                  disabled={!file || busy === "upload"}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </label>
            </div>
            <div className="row">
              <span className="bk-foot-note">
                {file ? `${file.name} · ${size(file.size)}` : "PDF up to 200 MB · text up to 20 MB"}
              </span>
              <span className="spacer" />
              <button className="btn primary" disabled={!file || busy === "upload"} onClick={() => void upload()}>
                {busy === "upload"
                  ? progress !== null && progress < 1
                    ? `Uploading ${Math.round(progress * 100)}%`
                    : "Queuing…"
                  : "Upload to the shelf"}
              </button>
            </div>
          </div>
        </div>

        <div className="bk-section">
          <h3>On the shelf · {shelf.books.length}</h3>
          {shelf.books.length === 0 && (
            <div className="bk-note">Nothing yet. Until a book is ready, the chat works from the spec library alone.</div>
          )}
          {shelf.books.length > 0 && (
            <div className="table">
              {shelf.books.map((b) => (
                <BookRow
                  key={b.bookId}
                  book={b}
                  busy={busy === b.bookId}
                  confirming={confirming === b.bookId}
                  onConfirm={() => setConfirming(b.bookId)}
                  onCancel={() => setConfirming(null)}
                  onRemove={() =>
                    void act(b.bookId, () => booksApi.remove(app.token, b.bookId), `${b.title} is coming off the shelf.`)
                  }
                  onRetry={() =>
                    void act(b.bookId, () => booksApi.resync(app.token, b.bookId), `${b.title} is queued for indexing again.`)
                  }
                />
              ))}
            </div>
          )}
          <div className="bk-foot-note">
            Storage is about {dollars(shelf.storageCentsPerMonth)} a month for {size(shelf.totalBytes)} (
            {dollars(shelf.storageCentsPerGbMonth)} per GB), and counts towards this month's AI budget. Each search
            the chat makes adds a fraction of a cent to the same budget.
          </div>
        </div>
      </div>
    </div>
  );
}

function BookRow(p: {
  readonly book: Book;
  readonly busy: boolean;
  readonly confirming: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly onRemove: () => void;
  readonly onRetry: () => void;
}) {
  const b = p.book;
  const s = STATUS[b.status] ?? STATUS.failed;
  return (
    <div className="trow bk-trow">
      <span className="bk-name">
        <span className="n">{b.title}</span>
        <span className="s">
          {b.filename} · {size(b.sizeBytes)} · added {b.createdAt.slice(0, 10)}
        </span>
        {b.error && <span className="s neg">{b.error}</span>}
        {b.warning && b.status === "ready" && <span className="s warn">{b.warning}</span>}
      </span>
      <span className="bk-format">
        <span className="chip">{b.format === "pdf" ? "PDF" : b.ext.toUpperCase()}</span>
      </span>
      <span className={`bk-status ${s.tone}`}>
        <span className="dot" style={{ background: "currentColor" }} /> {s.label}
      </span>
      <span className="bk-act">
        {/* Re-index a ready book too: the sync is incremental, so it costs
            nothing if the file is already in, and it recovers a book that a
            misconfigured sync marked ready without indexing. */}
        {(b.status === "failed" || b.status === "ready") && !p.confirming && (
          <button className="btn sm" disabled={p.busy} onClick={p.onRetry}>
            {b.status === "failed" ? "Retry" : "Re-index"}
          </button>
        )}
        {b.status !== "deleting" &&
          (p.confirming ? (
            <>
              <button className="btn sm" disabled={p.busy} onClick={p.onCancel}>
                Keep
              </button>
              <button className="btn sm danger" disabled={p.busy} onClick={p.onRemove}>
                {p.busy ? "Removing…" : "Remove"}
              </button>
            </>
          ) : (
            <button className="btn sm" disabled={p.busy} onClick={p.onConfirm}>
              Remove
            </button>
          ))}
      </span>
    </div>
  );
}
